/**
 * Custom hook for category autocomplete
 * 
 * Provides debounced autocomplete suggestions for category names from Elasticsearch
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import type { APINamedAutocompleteResponse } from '../../../generated-types/models/APINamedAutocompleteResponse'
import type { V1SearchItemsCategoriesAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

export interface CategorySuggestion {
  id: string
  name: string
  self: string
}

export interface CategoryAutocompleteResponse {
  suggestions: CategorySuggestion[]
  total: number
}

/**
 * Fetches category autocomplete suggestions
 */
const fetchCategoryAutocomplete = async (
  query: string,
  size = 10
): Promise<CategoryAutocompleteResponse> => {
  const response: APINamedAutocompleteResponse = await v1Api.v1SearchItemsCategoriesAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchItemsCategoriesAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      self: `${baseUrl}v1/items/categories/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for category name autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = useCategoryAutocomplete()
 * 
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * {suggestions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
 * ```
 */
export function useCategoryAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<CategoryAutocompleteResponse, Error>({
    queryKey: ['category-autocomplete', debouncedQuery],
    queryFn: () => fetchCategoryAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


